/**
 * Session storage for the access/refresh tokens + logged-in user.
 *
 * No httpOnly refresh cookie — `POST /auth/login` returns both tokens directly in the response
 * body, `POST /auth/refresh` exchanges the refresh token for a fresh pair (rotated — the old
 * refresh token is revoked the moment it's used), and `POST /auth/logout` expects the raw
 * refreshToken back in its request body. Tokens are persisted to localStorage so neither a page
 * reload nor the accessToken's short lifetime forces a re-login: the axios response interceptor
 * (lib/axios.ts) transparently calls `/auth/refresh` on a 401 and retries the failed request:
 * only when THAT fails (refresh token itself expired/revoked) does it clear the session here.
 */
const STORAGE_KEY = "auth-session";

interface StoredSession<TUser> {
  accessToken: string;
  refreshToken: string;
  user: TUser;
}

let session: StoredSession<unknown> | null = readFromStorage();

function readFromStorage(): StoredSession<unknown> | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredSession<unknown>) : null;
  } catch {
    return null;
  }
}

export function getAccessToken(): string | null {
  return session?.accessToken ?? null;
}

export function getRefreshToken(): string | null {
  return session?.refreshToken ?? null;
}

export function getStoredUser<TUser>(): TUser | null {
  return (session?.user as TUser | undefined) ?? null;
}

export function setSession<TUser>(next: StoredSession<TUser> | null): void {
  session = next;
  if (next) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
}

/** Replaces just the stored `user` (tokens untouched) — no-ops if there's no active session. */
export function updateStoredUser<TUser>(user: TUser): void {
  if (!session) return;
  session = { ...session, user };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}
